import palette from 'lib/styles/palette';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useDeviceList from 'hooks/device/useDeviceList';
import Loading from 'components/common/Loading';
import ErrorNoti from 'components/common/ErrorNoti';
import Pagination from 'components/common/Pagination';
import { useSelector } from 'react-redux';
import { SK_STORE_ID_LIST } from 'constants';
import { DGTAS_STORE_ID_LIST } from 'constants';

const Row = ({ device }) => {
  const navigate = useNavigate();

  return (
    <RowBlock
      registered={device.isDeviceCompleted === 1}
      onClick={() => {
        if (device.isDeviceCompleted === 1) {
          navigate(`${device.id}/register`);
        }
      }}
    >
      <div className="col">{device.company}</div>
      <div className="col">{device.name}</div>
      {device.isDeviceCompleted === 1 ? (
        <div className="small">완료</div>
      ) : (
        <button onClick={() => navigate(`${device.id}/register`)}>
          매칭하기
        </button>
      )}
    </RowBlock>
  );
};

const RowBlock = styled.div`
  padding: 15px 0;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  &:last-child {
    border-bottom: none;
  }

  button {
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background: ${palette.theme[3]};
    color: #fff;
    font-size: 10px;
    cursor: pointer;
    :hover {
      background: ${palette.theme[2]};
    }
  }

  .small {
    font-size: 14px;
    font-weight: bold;
    color: ${palette.theme[3]};
  }

  ${props =>
    props.registered &&
    css`
      cursor: pointer;
    `}
`;

const DeviceList = () => {
  const { partner } = useSelector(({ auth }) => auth);

  const {
    addDeviceListPage,
    deviceListPage,
    addDeviceListSearch,
    deviceListSearch,
    isLoading,
    error,
    addDeviceList,
    addDeviceListTotalPage,
    deviceList,
    deviceListTotalPage,
    setAddDeviceListPage,
    setDeviceListPage,
    onSearchAddDeviceList,
    onSearchDeviceList,
  } = useDeviceList();

  const addFilteredDeviceList =
    partner === 'seedn'
      ? addDeviceList?.rows
      : partner === 'sk'
      ? addDeviceList?.rows.filter(row => SK_STORE_ID_LIST.includes(row.id))
      : partner === 'dgtas'
      ? addDeviceList?.rows.filter(row => DGTAS_STORE_ID_LIST.includes(row.id))
      : [];

  const filteredDeviceList =
    partner === 'seedn'
      ? deviceList?.rows
      : partner === 'sk'
      ? deviceList?.rows.filter(row => SK_STORE_ID_LIST.includes(row.id))
      : partner === 'dgtas'
      ? deviceList?.rows.filter(row => DGTAS_STORE_ID_LIST.includes(row.id))
      : [];

  if (
    isLoading ||
    error?.response.status === 401 ||
    error?.response.data.error === 'AuthorizationNotFound'
  )
    return <Loading />;

  if (error?.response.status === 500) return <ErrorNoti />;

  return (
    <DeviceListBlock>
      <Header>장비 매칭 / 조회</Header>
      <Wrapper>
        <div className="header">
          <div className="title">장비 매칭</div>
          <StyledInput
            type="text"
            placeholder="회사명, 장소명을 입력해 주세요"
            value={addDeviceListSearch}
            onChange={onSearchAddDeviceList}
          />
        </div>
        <div className="table">
          <div className="table-header">
            <div className="col">회사명</div>
            <div className="col">설치 장소</div>
          </div>
          {addFilteredDeviceList.length === 0 ? (
            <div className="device-list-nothing">목록이 없습니다</div>
          ) : (
            <>
              {addFilteredDeviceList.map(device => (
                <Row key={device.id} device={device} />
              ))}
              <Pagination
                totalPage={addDeviceListTotalPage}
                currentPage={addDeviceListPage}
                setCurrentPage={setAddDeviceListPage}
              />
            </>
          )}
        </div>
      </Wrapper>
      <Wrapper>
        <div className="header">
          <div className="title">장비 조회</div>
          <StyledInput
            type="text"
            placeholder="회사명, 장소명을 입력해 주세요"
            value={deviceListSearch}
            onChange={onSearchDeviceList}
          />
        </div>
        <div className="table">
          <div className="table-header">
            <div className="col">회사명</div>
            <div className="col">설치 장소</div>
          </div>
          {filteredDeviceList.length === 0 ? (
            <div className="device-list-nothing">목록이 없습니다</div>
          ) : (
            <>
              {filteredDeviceList.map(device => (
                <Row key={device.id} device={device} />
              ))}
              <Pagination
                totalPage={deviceListTotalPage}
                currentPage={deviceListPage}
                setCurrentPage={setDeviceListPage}
              />
            </>
          )}
        </div>
      </Wrapper>
    </DeviceListBlock>
  );
};

export default DeviceList;

const DeviceListBlock = styled.div`
  width: 1080px;
  margin: 0 auto;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin: 2rem 0;
  font-size: 22px;
  font-weight: bold;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  & + & {
    margin-top: 2rem;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    font-size: 18px;
    font-weight: bold;
  }

  .table {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    border: solid 1px rgba(0, 0, 0, 0.08);
    border-radius: 20px;
    box-shadow: 0 0 50px 0 rgba(0, 0, 0, 0.08);
    text-align: center;
  }

  .table-header {
    display: flex;
    font-weight: bold;
    padding: 15px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .col {
    width: 40%;
  }

  .device-list-nothing {
    padding: 2rem 0;
    text-align: center;
    font-weight: 500;
  }
`;

const StyledInput = styled.input`
  width: 600px;
  height: 40px;
  padding: 15px;
  border: solid 1px #dfdfdf;
  border-radius: 11px;
  background: #fff;
  font-size: 16px;
  &::placeholder {
    color: #c6c6c6;
  }
`;
